import { useMutation, useQuery } from "@tanstack/react-query";

import useAuth from "~/hooks/auth/useAuth";
import queryClient from "~/lib/cache/queryClient";
import {
  getCollectionItemById,
  updateCollectionItemById,
} from "~/lib/payload/collection";
import type { User } from "~/types";

/**
 * Custom hook to manage the current user's data, including fetching and updating their information.
 *
 * @returns An object containing the current user's data, loading states, and functions to trigger updates.
 */
const useCurrentUser = () => {
  const {
    isLoading: authIsLoading,
    isAuthenticated: userIsAuthenticated,
    user,
  } = useAuth();

  const id: string | null = user?.id || null;

  const runQuery: boolean =
    userIsAuthenticated && Boolean(id) && !authIsLoading;
  const key: string = `current-user`;

  // Get data and cache it using TanStack Query.
  const { data: userData, isLoading: userDataIsLoading } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [key],
    queryFn: () =>
      getCollectionItemById({
        collection: "users",
        id: id || "",
        depth: 3,
        join: [
          {
            collection: "organization-permissions",
            limit: 1,
            depth: 3,
          },
        ],
      }),
    enabled: runQuery,
    refetchOnWindowFocus: false,
  });

  // Update data with mutation using TanStack Query.
  const { mutate: trigger, isPending: isMutating } = useMutation({
    mutationFn: (arg: Partial<User>) => {
      if (userIsAuthenticated) {
        return updateCollectionItemById({
          collection: "users",
          id: id || "",
          data: arg,
        });
      }
      return Promise.reject(new Error("User is not authenticated"));
    },
    onSuccess: () => {
      // Invalidate and refetch user data
      queryClient.invalidateQueries({ queryKey: [key] });
    },
  });

  const userRole = user?.role || null;

  return {
    data: {
      ...user,
      ...userData?.data,
      // Pick the correct organization based on the user's role.
      organization:
        userRole === "tenant"
          ? userData?.data?.leasePermissions?.docs?.[0]?.organization
          : userData?.data?.organizationPermissions?.docs?.[0]?.organization,
    } as User,
    isLoading: authIsLoading || userDataIsLoading,
    update: trigger,
    isUpdating: isMutating,
  };
};

export default useCurrentUser;
